import {
	type FrontendStage,
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import type { EligibleMessage } from '@post-office/shared-contracts/shared-request-middleware-context/context';

import { usePlacementApi } from '@post-office/use-placement-api';

import React, { useEffect, useState } from 'react';

import type { MessageContextWithFeatureId } from '@post-office/browser--townsquare/common/types';

const PROJECTS_APP_CHANGEBOARDING_POPUPS_MESSAGE_TEMPLATE_ID = 'projects-app-changeboarding-popups';

export type PlacementRenderProps = {
	dismiss: VoidFunction; // required
	children?: (dismiss: VoidFunction) => React.ReactNode;
	featureId?: string;
} & EligibleMessage;

export type AppProps = {
	children?: (dismiss: VoidFunction) => React.ReactNode;
	featureId?: string;
};

const placementConfig: PlacementBrowserConfig = {
	placementId: 'projects-screen-space-flags',
	options: {
		choreographer: {
			isDefaultEnabled: true,
		},
	},
};

const placement = { createFrontend: createPlacementFrontend(placementConfig) };

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: FrontendStage<PlacementRenderProps>;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	U
> = placement.createFrontend;

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	AppProps
> = createFrontend<AppProps>((Messages) => (appProps) => {
	const { featureId, children, ...otherAppProps } = appProps;
	const [messages, setMessages] = useState<EligibleMessage<Record<string, never>>[]>([]);

	const { data, error, isLoading } = usePlacementApi<{
		messages: Array<EligibleMessage<Record<string, never>>>;
	}>('/');

	useEffect(() => {
		if (isLoading || error) {
			return;
		}
		setMessages(data.messages);
	}, [data, error, isLoading]);

	if (error || isLoading || messages.length === 0) {
		return null;
	}

	const handleDismiss = (messageInstanceId: string): void => {
		setMessages((prevMessages) => {
			const filteredMessages = prevMessages.filter(
				(message) => messageInstanceId !== message.messageInstanceId,
			);
			return filteredMessages;
		});
	};

	const messageToRender = messages[0];
	if (
		messageToRender?.messageTemplateId === PROJECTS_APP_CHANGEBOARDING_POPUPS_MESSAGE_TEMPLATE_ID
	) {
		const projectsMessage = messageToRender as MessageContextWithFeatureId;
		if (!featureId || featureId !== projectsMessage.featureId) {
			return null;
		}
	} else if (featureId) {
		// if featureId is provided, but the message is not a projects-app-changeboarding-popups message, return null
		return null;
	}

	const childrenFunction = children || (() => null);

	return (
		<>
			{messageToRender ? (
				<Messages.render
					key={messageToRender.messageInstanceId}
					dismiss={() => handleDismiss(messageToRender.messageInstanceId)}
					{...messageToRender}
					{...otherAppProps}
				>
					{childrenFunction}
				</Messages.render>
			) : null}
		</>
	);
});
